import { ReactElement, useMemo } from 'react'
import { Flex, Text, useTooltip } from '@pancakeswap/uikit'
import styled, { DefaultTheme } from 'styled-components'
import { useConfig } from 'views/Predictions/context/ConfigProvider'
import { BigNumber } from 'ethers'
import { ethersToBigNumber } from 'utils/bigNumber'
import { REWARD_RATE } from 'state/predictions/config'
import { formatBnbv2, formatRoundTime } from 'views/Predictions/helpers'
import { useGetCurrentRoundCloseTimestamp, useGetIsClaimable } from 'state/predictions/hooks'
import useCountdown from 'views/Predictions/hooks/useCountdown'

type Status = 'expired' | 'live' | 'next' | 'soon' | 'canceled' | 'calculating'

interface CardHeaderProps {
  status: Status
  title: string
  epoch: number
  icon?: ReactElement
  winnerIcon?: ReactElement
  liveIcon?: ReactElement
  hasClaimed?: boolean
  trophyIcon?: ReactElement
  betAmount?: BigNumber
  multiplier?: string
  hasEnteredUp?: boolean
  hasEnteredDown?: boolean
}

const HEADER_HEIGHT = '64px'

// Used to get the gradient for the card border, which depends on the header color to create the illusion
// that header is overlapping the 1px card border.
// 'live' is not included into the switch case because it has isActive border style
export const getBorderBackground = (theme: DefaultTheme, status: Status) => {
  const gradientStopPoint = `calc(${HEADER_HEIGHT} + 1px)`
  switch (status) {
    case 'calculating':
      return `linear-gradient(transparent ${gradientStopPoint}, ${theme.colors.cardBorder} ${gradientStopPoint}), ${theme.colors.gradientCardHeader}`
    case 'canceled':
      return `linear-gradient(${theme.colors.warning} ${gradientStopPoint}, ${theme.colors.cardBorder} ${gradientStopPoint})`
    case 'next':
      return `linear-gradient(${theme.colors.secondary} ${gradientStopPoint}, ${theme.colors.cardBorder} ${gradientStopPoint})`
    case 'expired':
    case 'soon':
    default:
      return theme.colors.cardBorder
  }
}

const getBackgroundColor = (theme: DefaultTheme, status: Status) => {
  switch (status) {
    case 'calculating':
      return theme.colors.gradientCardHeader
    case 'live':
      return 'transparent'
    case 'canceled':
      return theme.colors.warning
    case 'next':
      return theme.colors.secondary
    case 'expired':
    case 'soon':
    default:
      return theme.colors.cardBorder
  }
}

type TextColor = 'textDisabled' | 'white' | 'secondary' | 'text' | 'textSubtle'
type FallbackColor = 'text' | 'textSubtle'

const getTextColorByStatus = (status: Status, fallback: FallbackColor): TextColor => {
  switch (status) {
    case 'expired':
      return 'textDisabled'
    case 'next':
      return 'white'
    case 'live':
      return 'secondary'
    case 'canceled':
    case 'calculating':
      return 'text'
    default:
      return fallback
  }
}

const StyledCardHeader = styled.div<{ status: Status }>`
  align-items: center;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${HEADER_HEIGHT};
  padding: 20px 10px;
  gap: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
`

const StyledFlex = styled(Flex)`
  display: flex;
  padding: 2px 8px 2px 4px;
  align-items: center;
  gap: 2px;
  border-radius: 100px;
  border: 1px solid ${({ theme }) => theme.colors.liquidityBorder};
  background: linear-gradient(180deg, rgba(243, 238, 255, 0.00) 0%, rgba(243, 238, 255, 0.04) 100%), rgba(147, 130, 255, 0.01);
  box-shadow: 0px 0px 12px 0px rgba(255, 255, 255, 0.08) inset; 
  height: 24px;
  div {
    font-family: DM Sans;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 100%;
    text-transform: uppercase; 
    color: ${({ theme }) => theme.colors.textSubtle};
  }
`

const Round = styled.div`
  justify-self: center;
`

const CardHeader: React.FC<React.PropsWithChildren<CardHeaderProps>> = ({ 
    status, title, epoch, icon, hasClaimed, winnerIcon, liveIcon, trophyIcon, betAmount, multiplier, hasEnteredUp, hasEnteredDown
  }) => {
  const currentRoundCloseTimestamp = useGetCurrentRoundCloseTimestamp()
  const { secondsRemaining } = useCountdown(currentRoundCloseTimestamp)
  const countdown = formatRoundTime(secondsRemaining)
  const { token, displayedDecimals } = useConfig()
  const isClaimable = useGetIsClaimable(epoch)
  const formattedAmount = useMemo(() => {
    let tokenAmount
    if (hasClaimed) {
      if (betAmount) {
        const multiplierNumber = parseFloat(multiplier)
        tokenAmount = BigNumber.from(
          ethersToBigNumber(betAmount)
            .times(Number.isFinite(multiplierNumber) ? multiplierNumber * REWARD_RATE : 1)
            .toFixed(0),
        )
      }
    } else {
      tokenAmount = betAmount
    }
    return formatBnbv2(tokenAmount, displayedDecimals)
  }, [betAmount, displayedDecimals, hasClaimed, multiplier])

  const { targetRef, tooltipVisible, tooltip } = useTooltip(
    <div style={{ whiteSpace: 'nowrap' }}>{`${formattedAmount} ${token.symbol}`}</div>,
    { placement: 'bottom' },
  )

  const textColor = getTextColorByStatus(status, 'text')
  const isLive = status === 'live'

  return (
    <StyledCardHeader style={{justifyContent: (hasClaimed || isLive || isClaimable) ? 'space-between' : 'center'}} status={status}>
      <Flex style={{gap: '10px'}}>
        <StyledFlex>
          {icon}
          <Text>
            {title}
          </Text>
        </StyledFlex>
        {hasClaimed && (
          <StyledFlex ref={targetRef}>
            {winnerIcon}
            <Text>
              Claimed
            </Text>
            {tooltipVisible && tooltip}
          </StyledFlex>
        )}
        {(hasEnteredUp || hasEnteredDown) && (
          <StyledFlex>
            {hasEnteredUp ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 7L15 12L5 12L10 7Z" fill="#66FFB6"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 13L5 8L15 8L10 13Z" fill="#FF4D4D"/>
              </svg>
            )
            }
            <Text>
            {hasEnteredUp ? 'Up' : 'Down'} Entered 
            </Text>
          </StyledFlex>
        )}
      </Flex>

      {hasClaimed && (
        trophyIcon
      )}

      {isLive && (
        <Text>
          {countdown}
        </Text>
      )}
      {isClaimable && (
        <Flex ref={targetRef} alignItems='center'>
          <Text mr='5px' className='syncopate' color='yellow' fontWeight={700}>
            WIN
          </Text>
          {trophyIcon}
          {tooltipVisible && tooltip}
        </Flex>
      )}
    </StyledCardHeader>
  )
}

export default CardHeader
